/**
 * Character profiles: create, list, load, save, delete.
 *
 * The one place that catches. A backend throws whatever the storage API threw and this turns it
 * into a `SaveFailure` a screen can read out, which is why a full store says "full" and a record
 * from a newer build says so rather than both arriving as a stack trace.
 *
 * Every write is staged and then swapped, and the swap is the only moment the live record changes.
 * A crash before it leaves the previous profile intact and an orphan staging record behind, so
 * `openProfileStore` sweeps those on the way in: a staged record is a write that never landed, and
 * a write that never landed is not a save.
 *
 * A record that reads as `fromNewerBuild` is never written over. That is the one refusal with
 * teeth: two tabs on two builds is an ordinary accident, and overwriting is not an accident anyone
 * recovers from.
 *
 * The clock and the id source are injected. Neither is a determinism rule here, this is not sim
 * code, but a test that cannot say what time it is cannot assert on `savedAtMs`.
 */

import { readProfile } from '@/save/readProfile';
import { newProfile, summarize, toSaveRecord } from '@/save/saveContract';
import { SAVE_MIGRATIONS } from '@/save/saveMigrationRegistry';
import { detailOf, isQuotaExceeded, isStagingKey, stagingKeyFor } from '@/save/saveBackendContract';
import { refused, saved } from '@/save/saveResult';
import type { SaveMigration } from '@/save/migrationContract';
import type { ProfileSummary, SaveProfile } from '@/save/saveContract';
import type { SaveBackend } from '@/save/saveBackendContract';
import type { SaveResult } from '@/save/saveResult';

export interface ProfileStore {
  /** A fresh character under a rolled name, written before it is handed back. */
  create(name: string): Promise<SaveResult<SaveProfile>>;
  /** Every readable profile, newest save first. Unreadable records are skipped, never deleted. */
  list(): Promise<SaveResult<readonly ProfileSummary[]>>;
  load(profileId: string): Promise<SaveResult<SaveProfile>>;
  /** Stamps `savedAtMs`, stages, swaps. The stored profile comes back. */
  save(profile: SaveProfile): Promise<SaveResult<SaveProfile>>;
  remove(profileId: string): Promise<SaveResult<void>>;
  close(): void;
}

export interface ProfileStoreOptions {
  readonly now?: () => number;
  readonly newId?: () => string;
  readonly chain?: readonly SaveMigration[];
}

/**
 * Opens a store over a backend, sweeping any staging record left by a write that never finished.
 *
 * The sweep is not a repair. It discards a record that was never live, which is the state the
 * player was already in when the tab died.
 */
export async function openProfileStore(
  backend: SaveBackend,
  options: ProfileStoreOptions = {},
): Promise<ProfileStore> {
  const now = options.now ?? ((): number => Date.now());
  const newId = options.newId ?? ((): string => crypto.randomUUID());
  const chain = options.chain ?? SAVE_MIGRATIONS;

  await sweepStaging(backend);

  const write = async (profile: SaveProfile): Promise<SaveResult<SaveProfile>> => {
    const stamped: SaveProfile = { ...profile, savedAtMs: now() };
    const stagingKey = stagingKeyFor(stamped.profileId);

    try {
      await backend.stage(stagingKey, toSaveRecord(stamped));
    } catch (error) {
      if (isQuotaExceeded(error)) {
        return refused({ reason: 'quotaExceeded', profileId: stamped.profileId });
      }
      return refused({ reason: 'storageFailed', operation: 'stage', detail: detailOf(error) });
    }

    try {
      await backend.commit(stagingKey, stamped.profileId);
    } catch (error) {
      // The live record is whatever it was. Drop the staged copy so the next open has nothing to
      // sweep, and say the write failed rather than pretending it partly happened.
      await discard(backend, stagingKey);
      if (isQuotaExceeded(error)) {
        return refused({ reason: 'quotaExceeded', profileId: stamped.profileId });
      }
      return refused({ reason: 'storageFailed', operation: 'commit', detail: detailOf(error) });
    }

    return saved(stamped);
  };

  const read = async (profileId: string): Promise<SaveResult<SaveProfile>> => {
    let raw: unknown;
    try {
      raw = await backend.read(profileId);
    } catch (error) {
      return refused({ reason: 'storageFailed', operation: 'read', detail: detailOf(error) });
    }

    if (raw === null) return refused({ reason: 'noSuchProfile', profileId });
    return readProfile(raw, chain);
  };

  return {
    create(name: string): Promise<SaveResult<SaveProfile>> {
      return write(newProfile(newId(), name, now()));
    },

    load: read,

    async save(profile: SaveProfile): Promise<SaveResult<SaveProfile>> {
      const existing = await read(profile.profileId);
      if (!existing.ok && existing.failure.reason === 'fromNewerBuild') return existing;
      return write(profile);
    },

    async list(): Promise<SaveResult<readonly ProfileSummary[]>> {
      let keys: readonly string[];
      try {
        keys = await backend.keys();
      } catch (error) {
        return refused({ reason: 'storageFailed', operation: 'keys', detail: detailOf(error) });
      }

      const summaries: ProfileSummary[] = [];
      for (const key of keys) {
        if (isStagingKey(key)) continue;
        const profile = await read(key);
        // A record this build cannot read is not a row in a picker, and it is not this call's
        // business to delete it either. It stays where it is, and `load` still says why.
        if (profile.ok) summaries.push(summarize(profile.value));
      }

      summaries.sort(byNewestSave);
      return saved(summaries);
    },

    async remove(profileId: string): Promise<SaveResult<void>> {
      try {
        await backend.remove(profileId);
        await backend.remove(stagingKeyFor(profileId));
      } catch (error) {
        return refused({ reason: 'storageFailed', operation: 'remove', detail: detailOf(error) });
      }
      return saved(undefined);
    },

    close(): void {
      backend.close();
    },
  };
}

/** Newest save first, and by id when two land on the same millisecond, so the order is stable. */
function byNewestSave(left: ProfileSummary, right: ProfileSummary): number {
  if (left.savedAtMs !== right.savedAtMs) return right.savedAtMs - left.savedAtMs;
  return left.profileId < right.profileId ? -1 : 1;
}

async function sweepStaging(backend: SaveBackend): Promise<void> {
  const keys = await backend.keys();
  for (const key of keys) {
    if (isStagingKey(key)) await backend.remove(key);
  }
}

/** Best effort. A staging record that will not delete is swept on the next open. */
async function discard(backend: SaveBackend, key: string): Promise<void> {
  try {
    await backend.remove(key);
  } catch {
    // Nothing useful to do here, and the caller already has the failure that matters.
  }
}
